// src/layout/AppLayout.js
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useRealtime } from "../context/RealtimeContext";
import { useROStore } from "../context/ROContext";
import { dealershipRoleLabel } from "../utils/roleLabels";
import "./AppLayout.css";

function prettyLabel(input) {
  const raw = String(input || "").trim();
  if (!raw) return "—";
  return raw
    .replace(/[_-]+/g, " ")
    .toLowerCase()
    .split(" ")
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
}

function safeString(v) {
  return typeof v === "string" ? v : v == null ? "" : String(v);
}

function safeNumber(v, fallback = 0) {
  const n = Number(v);
  return Number.isFinite(n) ? n : fallback;
}

function todayLocalISO() {
  const d = new Date();
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

function dateToLocalISO(date) {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return "";
  const yyyy = date.getFullYear();
  const mm = String(date.getMonth() + 1).padStart(2, "0");
  const dd = String(date.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

function normalizeDateOnly(value) {
  if (!value) return "";

  if (value instanceof Date) {
    return dateToLocalISO(value);
  }

  const raw = safeString(value).trim();
  if (!raw) return "";

  const isoMatch = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (isoMatch) return `${isoMatch[1]}-${isoMatch[2]}-${isoMatch[3]}`;

  const parsed = new Date(raw);
  if (!Number.isNaN(parsed.getTime())) {
    return dateToLocalISO(parsed);
  }

  return "";
}

function normalizeDeferredStatus(v) {
  const s = safeString(v).trim().toLowerCase();
  if (s === "followed_up") return "followed_up";
  if (s === "closed") return "closed";
  return "open";
}

function isDeferredItemDue(row) {
  const r = row && typeof row === "object" ? row : {};
  const status = normalizeDeferredStatus(r.status);

  if (status !== "open") return false;

  const followUpDate = normalizeDateOnly(
    r.followUpDate || r.follow_up_date || r.follow_up || r.dueDate || r.due_date
  );

  if (!followUpDate) return false;

  return followUpDate <= todayLocalISO();
}

function normalizeStoreRole(v) {
  const raw = safeString(v).trim();
  const r = raw.toLowerCase().replace(/[_-]+/g, " ").replace(/\s+/g, " ");

  if (r === "tech") return "technician";
  if (r === "service manager") return "manager";
  if (r === "store manager") return "manager";
  if (r === "service advisor") return "advisor";
  if (r === "advisor") return "advisor";
  if (r === "parts advisor") return "staff";
  if (r === "parts staff") return "staff";
  if (r === "parts manager") return "parts";
  if (r === "shop foreman") return "foreman";

  return r;
}

function getDisplayName(u) {
  const dn = safeString(u?.displayName).trim();
  if (dn) return dn;

  const first = safeString(u?.firstName).trim();
  const last = safeString(u?.lastName).trim();
  const full = [first, last].filter(Boolean).join(" ").trim();
  if (full) return full;

  const name = safeString(u?.name || u?.fullName).trim();
  if (name) return name;

  const email = safeString(u?.email).trim();
  return email || "User";
}

function storeRoleLabel(role) {
  const r = normalizeStoreRole(role);
  return dealershipRoleLabel(r) || prettyLabel(r);
}

function canAccess(role, allowedRoles) {
  const normalized = normalizeStoreRole(role);
  return allowedRoles.includes(normalized);
}

function getStoreLandingPath(role) {
  const normalized = normalizeStoreRole(role);

  if (normalized === "advisor") return "/advisor-log";
  if (normalized === "parts" || normalized === "staff") return "/parts";
  if (normalized === "technician") return "/tech";
  if (normalized === "foreman") return "/shop-foreman";
  if (normalized === "dispatcher") return "/dispatch";

  return "/dashboard";
}

function getSyncState(store) {
  const error = safeString(store?.lastSyncError).trim();
  const isSaving = !!store?.isSaving;

  if (typeof navigator !== "undefined" && navigator.onLine === false) {
    return {
      key: "offline",
      label: "Offline",
      detail: "Showing cached data. Changes may not sync until connection returns.",
      tone: "warn",
      show: true,
    };
  }

  if (error) {
    return {
      key: "error",
      label: "Sync issue",
      detail: error,
      tone: "danger",
      show: true,
    };
  }

  if (isSaving) {
    return {
      key: "saving",
      label: "Saving…",
      detail: "Flowtrak is saving your latest change.",
      tone: "working",
      show: true,
    };
  }

  return {
    key: "ready",
    label: "Live",
    detail: "Connected to Flowtrak.",
    tone: "neutral",
    show: false,
  };
}

function SyncStatusBar({ store, isImpersonating }) {
  const sync = getSyncState(store);

  if (!sync.show && !isImpersonating) return null;

  const toneStyle = (() => {
    if (sync.tone === "danger") {
      return {
        border: "1px solid rgba(180,35,24,0.18)",
        background:
          "linear-gradient(180deg, rgba(254,243,242,0.96), rgba(255,255,255,0.98))",
        color: "#b42318",
      };
    }

    if (sync.tone === "warn") {
      return {
        border: "1px solid rgba(181,71,8,0.16)",
        background:
          "linear-gradient(180deg, rgba(255,247,237,0.96), rgba(255,255,255,0.98))",
        color: "#b54708",
      };
    }

    if (sync.tone === "working") {
      return {
        border: "1px solid rgba(37,99,235,0.14)",
        background:
          "linear-gradient(180deg, rgba(239,246,255,0.92), rgba(255,255,255,0.98))",
        color: "#1d4ed8",
      };
    }

    return {
      border: "1px solid rgba(15,23,42,0.08)",
      background: "#ffffff",
      color: "rgba(15,23,42,0.66)",
    };
  })();

  return (
    <div
      style={{
        ...toneStyle,
        borderRadius: 12,
        padding: "8px 10px",
        marginBottom: 10,
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 12,
        boxShadow: "0 6px 18px rgba(15,23,42,0.035)",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
        <span
          style={{
            width: 7,
            height: 7,
            borderRadius: 999,
            background: "currentColor",
            flex: "0 0 auto",
            opacity: sync.tone === "working" ? 0.74 : 0.9,
          }}
        />

        <div style={{ minWidth: 0 }}>
          <div
            style={{
              fontSize: 11,
              fontWeight: 900,
              letterSpacing: "0.10em",
              textTransform: "uppercase",
              lineHeight: 1.1,
            }}
          >
            {isImpersonating ? "Support mode" : sync.label}
          </div>

          <div
            style={{
              marginTop: 3,
              fontSize: 11.2,
              lineHeight: 1.3,
              color: "rgba(15,23,42,0.64)",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              maxWidth: 760,
            }}
            title={isImpersonating ? "Editing is disabled while impersonating." : sync.detail}
          >
            {isImpersonating ? "Editing is disabled while impersonating." : sync.detail}
          </div>
        </div>
      </div>

      {sync.tone === "danger" && typeof store?.refreshStoreData === "function" ? (
        <button
          type="button"
          onClick={() => {
            store.refreshStoreData().catch(() => {});
          }}
          style={{
            border: "1px solid rgba(180,35,24,0.18)",
            background: "#fff",
            color: "#b42318",
            borderRadius: 8,
            padding: "7px 10px",
            fontSize: 11,
            fontWeight: 850,
            cursor: "pointer",
            whiteSpace: "nowrap",
          }}
        >
          Retry sync
        </button>
      ) : null}
    </div>
  );
}

function SidebarBadge({ count }) {
  const n = safeNumber(count, 0);
  if (n <= 0) return null;

  return (
    <span
      style={{
        minWidth: 20,
        height: 20,
        padding: "0 7px",
        borderRadius: 999,
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        background: "#dc2626",
        color: "#ffffff",
        fontSize: 11,
        fontWeight: 900,
        lineHeight: 1,
        boxShadow: "0 0 0 1px rgba(255,255,255,0.12)",
      }}
      aria-label={`${n} deferred follow-up${n === 1 ? "" : "s"} due`}
      title={`${n} deferred follow-up${n === 1 ? "" : "s"} due`}
    >
      {n > 99 ? "99+" : n}
    </span>
  );
}

function RealtimeStatusPill({ realtime }) {
  if (!realtime?.isEnabled) return null;

  const stateClass = realtime.isLive
    ? "sidebar-realtime--live"
    : realtime.isReconnecting || realtime.isConnecting
    ? "sidebar-realtime--reconnecting"
    : "sidebar-realtime--offline";

  return (
    <div
      className={`sidebar-realtime ${stateClass}`}
      aria-label={`Realtime connection: ${realtime.label}`}
      title={`Realtime connection: ${realtime.label}`}
    >
      <span className="sidebar-realtime-dot" aria-hidden="true" />
      <span className="sidebar-realtime-label">{realtime.label}</span>
    </div>
  );
}

const LOGO_ICON_SRC = "/logo-icon.png";
const LOGO_TEXT_SRC = "/logo-text.png";

export default function AppLayout() {
  const nav = useNavigate();
  const loc = useLocation();
  const store = useROStore();
  const auth = useAuth();
  const realtime = useRealtime();

  const isImpersonating = !!auth?.isImpersonating;

  const user = auth?.user || null;
  const membership = auth?.membership || null;
  const stores = useMemo(
    () => (Array.isArray(auth?.stores) ? auth.stores : []),
    [auth?.stores]
  );

  const availableStores = useMemo(() => {
    return stores.filter((row) => {
      const storeId = safeString(row?.storeId || row?.store_id).trim();
      const status = safeString(row?.status || "ACTIVE").trim().toUpperCase();
      const isActive =
        typeof row?.isActive === "boolean"
          ? row.isActive
          : typeof row?.is_active === "boolean"
          ? row.is_active
          : true;

      return !!storeId && isActive !== false && status === "ACTIVE";
    });
  }, [stores]);

  const hasMultipleStores = availableStores.length > 1;

  const effectiveStoreId = safeString(
    auth?.effectiveStoreId || auth?.activeStoreId || user?.storeId
  ).trim();

  const clearSession = auth?.clearSession || auth?.signOut;
  const platformRole = safeString(auth?.platformRole || user?.platformRole).trim();
  const hasPlatformAccess = !!platformRole && !isImpersonating;

  const mode = auth?.mode === "platform" ? "platform" : "store";
  const pathIsPlatform = loc.pathname.startsWith("/platform");
  const isUsersPath = loc.pathname.startsWith("/platform/users");

  useEffect(() => {
    if (pathIsPlatform) {
      if (!hasPlatformAccess) {
        if (typeof auth?.setStoreMode === "function") auth.setStoreMode();
        nav("/dashboard", { replace: true });
        return;
      }

      if (mode !== "platform" && typeof auth?.setPlatformMode === "function") {
        auth.setPlatformMode();
      }
      return;
    }

    if (mode === "platform" && typeof auth?.setStoreMode === "function") {
      auth.setStoreMode();
    }
  }, [pathIsPlatform, hasPlatformAccess, mode, auth, nav]);

  const storeUser = useMemo(() => {
    return store?.currentUser && typeof store.currentUser === "object"
      ? store.currentUser
      : null;
  }, [store?.currentUser]);

  const sidebarIdentity = useMemo(() => {
    return user || storeUser || null;
  }, [user, storeUser]);

  const displayName = getDisplayName(sidebarIdentity);

  const matchedStore = useMemo(() => {
    if (!effectiveStoreId) return null;
    return (
      stores.find(
        (s) => safeString(s?.storeId || s?.store_id).trim() === effectiveStoreId
      ) || null
    );
  }, [stores, effectiveStoreId]);

  const storeName = safeString(
    membership?.storeName ||
      membership?.store_name ||
      matchedStore?.storeName ||
      matchedStore?.store_name ||
      store?.currentStore?.dealerName ||
      store?.currentStore?.dealer_name ||
      store?.currentStore?.name ||
      user?.storeName
  ).trim();

  const storeRoleRaw = safeString(
    membership?.role ||
      matchedStore?.role ||
      user?.storeRole ||
      storeUser?.role ||
      user?.role ||
      "manager"
  ).trim();

  const storeRole = normalizeStoreRole(storeRoleRaw);
  const storeRoleNice = storeRoleLabel(storeRole);

  const platformRoleNice = prettyLabel(platformRole || "PLATFORM_ADMIN");
  const inPlatformMode = hasPlatformAccess && mode === "platform";
  const isSuperAdmin = String(platformRole || "").toUpperCase() === "SUPER_ADMIN";
  const showRealtimeStatus = !inPlatformMode && realtime?.isEnabled;

  const badgeText = inPlatformMode ? "Platform" : storeName || "Store";
  const roleLine = inPlatformMode ? platformRoleNice : storeRoleNice;

  function goStoreMode() {
    if (typeof auth?.setStoreMode === "function") auth.setStoreMode();

    if (storeRole === "advisor") {
      nav("/advisor-log");
      return;
    }

    if (storeRole === "parts" || storeRole === "staff") {
      nav("/parts");
      return;
    }

    if (storeRole === "technician") {
      nav("/tech");
      return;
    }

    nav("/dashboard");
  }

  function goPlatformMode() {
    if (!hasPlatformAccess) return;
    if (typeof auth?.setPlatformMode === "function") auth.setPlatformMode();
    nav("/platform/admin");
  }

  function handleStoreChange(event) {
    const nextStoreId = safeString(event?.target?.value).trim();
    if (!nextStoreId || nextStoreId === effectiveStoreId) return;
    if (typeof auth?.setActiveStoreId !== "function") return;

    const nextStore =
      availableStores.find(
        (row) =>
          safeString(row?.storeId || row?.store_id).trim() === nextStoreId
      ) || null;

    const resolvedStoreId = auth.setActiveStoreId(nextStoreId);
    if (!resolvedStoreId) return;

    const nextRole = safeString(nextStore?.role || "manager").trim();

    // Hard navigation is intentional here. It clears any store-scoped page/context
    // state so data from the previous store cannot flash or linger after switching.
    window.location.assign(getStoreLandingPath(nextRole));
  }

  const navLinkClass = ({ isActive }) =>
    isActive ? "sidebar-link sidebar-link-active" : "sidebar-link";

  const showDashboard = canAccess(storeRole, [
    "manager",
    "parts",
    "foreman",
    "dispatcher",
    "admin",
  ]);

  const showWipBoard = canAccess(storeRole, [
    "manager",
    "advisor",
    "technician",
    "dispatcher",
    "foreman",
    "parts",
    "staff",
    "admin",
  ]);

  const showRoEntry = canAccess(storeRole, ["manager", "advisor"]);

  const showAdvisorDashboard = canAccess(storeRole, [
    "manager",
    "advisor",
    "admin",
  ]);

  const showAdvisorDeferred = canAccess(storeRole, ["advisor"]);

  const showPartsManagerDashboard = canAccess(storeRole, [
    "manager",
    "parts",
    "dispatcher",
    "admin",
  ]);

  const showShopForemanDashboard = canAccess(storeRole, [
    "manager",
    "foreman",
    "dispatcher",
    "admin",
  ]);

  const showPartsQueue = canAccess(storeRole, ["parts", "staff"]);

  const showTechConsole = canAccess(storeRole, [
    "manager",
    "dispatcher",
    "technician",
  ]);

  const showTechStats = canAccess(storeRole, [
    "manager",
    "foreman",
    "dispatcher",
    "technician",
    "admin",
  ]);

  const showRoSearch = canAccess(storeRole, [
    "manager",
    "advisor",
    "technician",
    "dispatcher",
    "foreman",
    "parts",
    "staff",
    "admin",
  ]);

  const showShopScreen = canAccess(storeRole, [
    "manager",
    "parts",
    "foreman",
    "dispatcher",
    "admin",
  ]);

  const showStaff = canAccess(storeRole, ["manager", "parts", "foreman", "admin"]);

  const [deferredDueCount, setDeferredDueCount] = useState(0);

  const refreshDeferredDueCount = useCallback(async () => {
    if (!showAdvisorDeferred || inPlatformMode || isImpersonating) {
      setDeferredDueCount(0);
      return;
    }

    if (typeof auth?.apiGet !== "function") {
      setDeferredDueCount(0);
      return;
    }

    try {
      const data = await auth.apiGet("/api/advisor/deferred-services?status=open");
      const items = Array.isArray(data?.items) ? data.items : [];
      const dueFromItems = items.filter((item) => isDeferredItemDue(item)).length;
      setDeferredDueCount(dueFromItems);
    } catch {
      setDeferredDueCount(0);
    }
  }, [auth, showAdvisorDeferred, inPlatformMode, isImpersonating]);

  useEffect(() => {
    refreshDeferredDueCount();

    if (!showAdvisorDeferred || inPlatformMode || isImpersonating) return undefined;

    const intervalId = window.setInterval(() => {
      refreshDeferredDueCount();
    }, 60000);

    function onDeferredUpdated() {
      refreshDeferredDueCount();
    }

    window.addEventListener("flowtrak:deferred-services-updated", onDeferredUpdated);

    return () => {
      window.clearInterval(intervalId);
      window.removeEventListener(
        "flowtrak:deferred-services-updated",
        onDeferredUpdated
      );
    };
  }, [
    refreshDeferredDueCount,
    showAdvisorDeferred,
    inPlatformMode,
    isImpersonating,
    loc.pathname,
  ]);

  return (
    <div className="app-shell">
      <aside className="sidebar">
        <div className="sidebar-brandPanel">
          <div className="sidebar-brand">
            <img
              className="sidebar-logo-icon"
              src={LOGO_ICON_SRC}
              alt="Flowtrak icon"
            />
            <img
              className="sidebar-logo-text"
              src={LOGO_TEXT_SRC}
              alt="Flowtrak"
            />
            <div className="sidebar-badge" title={badgeText}>
              {badgeText}
            </div>
          </div>

          <div className="sidebar-signedin">
            <div className="sidebar-name">{displayName}</div>
            <div className="sidebar-role">{roleLine}</div>
          </div>

          {showRealtimeStatus ? <RealtimeStatusPill realtime={realtime} /> : null}

          {!inPlatformMode && hasMultipleStores ? (
            <div
              style={{
                marginTop: 10,
                width: "100%",
                padding: "0 4px",
              }}
            >
              <label
                htmlFor="flowtrak-store-switcher"
                style={{
                  display: "block",
                  marginBottom: 5,
                  paddingLeft: 2,
                  fontSize: 9.5,
                  fontWeight: 800,
                  letterSpacing: "0.10em",
                  textTransform: "uppercase",
                  color: "rgba(255,255,255,0.52)",
                }}
              >
                Switch store
              </label>

              <select
                id="flowtrak-store-switcher"
                value={
                  effectiveStoreId ||
                  safeString(
                    availableStores[0]?.storeId || availableStores[0]?.store_id
                  ).trim()
                }
                onChange={handleStoreChange}
                aria-label="Switch active store"
                style={{
                  width: "100%",
                  minWidth: 0,
                  height: 34,
                  padding: "0 30px 0 9px",
                  borderRadius: 8,
                  border: "1px solid rgba(255,255,255,0.12)",
                  background: "rgba(255,255,255,0.07)",
                  color: "#ffffff",
                  fontSize: 11.5,
                  fontWeight: 700,
                  cursor: "pointer",
                }}
              >
                {availableStores.map((row) => {
                  const rowStoreId = safeString(
                    row?.storeId || row?.store_id
                  ).trim();
                  const rowStoreName = safeString(
                    row?.storeName || row?.store_name || row?.storeCode || row?.store_code
                  ).trim();
                  const rowGroupName = safeString(
                    row?.groupName || row?.group_name
                  ).trim();
                  const optionLabel =
                    rowGroupName &&
                    rowStoreName &&
                    rowGroupName.toLowerCase() !== rowStoreName.toLowerCase()
                      ? `${rowGroupName} / ${rowStoreName}`
                      : rowStoreName || "Store";

                  return (
                    <option key={rowStoreId} value={rowStoreId} style={{ color: "#111827" }}>
                      {optionLabel}
                    </option>
                  );
                })}
              </select>
            </div>
          ) : null}

          {hasPlatformAccess ? (
            <div
              className="sidebar-toggle"
              role="tablist"
              aria-label="Workspace mode"
            >
              <button
                type="button"
                onClick={goStoreMode}
                className={
                  !inPlatformMode ? "toggle-btn toggle-btn-active" : "toggle-btn"
                }
              >
                Store
              </button>

              <button
                type="button"
                onClick={goPlatformMode}
                className={
                  inPlatformMode ? "toggle-btn toggle-btn-active" : "toggle-btn"
                }
              >
                Platform
              </button>
            </div>
          ) : null}

          <div className="sidebar-divider" />
        </div>

        {inPlatformMode ? (
          <>
            <div className="sidebar-section">Platform</div>

            <nav className="sidebar-nav">
              <NavLink to="/platform/admin" className={navLinkClass}>
                Admin Home
              </NavLink>

              <NavLink
                to="/platform/users"
                className={({ isActive }) =>
                  isUsersPath || isActive
                    ? "sidebar-link sidebar-link-active"
                    : "sidebar-link"
                }
              >
                Users
              </NavLink>

              <NavLink to="/platform/business-units" className={navLinkClass}>
                Business Units
              </NavLink>

              {isSuperAdmin ? (
                <NavLink to="/platform/billing" className={navLinkClass}>
                  Billing
                </NavLink>
              ) : null}
            </nav>

            <div className="sidebar-section">Account</div>

            <nav className="sidebar-nav">
              <NavLink to="/settings" className={navLinkClass}>
                Settings
              </NavLink>
            </nav>

            <button
              type="button"
              className="sidebar-btn sidebar-btn-danger"
              onClick={() => {
                if (typeof clearSession === "function") clearSession();
                nav("/login");
              }}
            >
              Log out
            </button>
          </>
        ) : (
          <>
            {showDashboard || showWipBoard || showRoEntry ? (
              <>
                <div className="sidebar-section">Shop</div>

                <nav className="sidebar-nav">
                  {showDashboard ? (
                    <NavLink to="/dashboard" className={navLinkClass}>
                      Dashboard
                    </NavLink>
                  ) : null}

                  {showWipBoard ? (
                    <NavLink to="/dispatch" className={navLinkClass}>
                      WIP Board
                    </NavLink>
                  ) : null}

                  {showRoEntry ? (
                    <NavLink to="/ro-entry" className={navLinkClass}>
                      RO Entry
                    </NavLink>
                  ) : null}
                </nav>
              </>
            ) : null}

            {showAdvisorDashboard ||
            showAdvisorDeferred ||
            showPartsManagerDashboard ||
            showShopForemanDashboard ? (
              <>
                <div className="sidebar-section">Role Dashboards</div>

                <nav className="sidebar-nav">
                  {showAdvisorDashboard ? (
                    <>
                      <NavLink to="/advisor" className={navLinkClass}>
                        Advisor Dashboard
                      </NavLink>

                      <NavLink to="/advisor-log" className={navLinkClass}>
                        Advisor Log
                      </NavLink>
                    </>
                  ) : null}

                  {showAdvisorDeferred ? (
                    <NavLink to="/advisor-deferred" className={navLinkClass}>
                      <span
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          gap: 8,
                          width: "100%",
                        }}
                      >
                        <span>Deferred Follow-Up</span>
                        <SidebarBadge count={deferredDueCount} />
                      </span>
                    </NavLink>
                  ) : null}

                  {showPartsManagerDashboard ? (
                    <NavLink to="/parts-manager" className={navLinkClass}>
                      Parts Manager
                    </NavLink>
                  ) : null}

                  {showShopForemanDashboard ? (
                    <NavLink to="/shop-foreman" className={navLinkClass}>
                      Shop Foreman
                    </NavLink>
                  ) : null}
                </nav>
              </>
            ) : null}

            {showPartsQueue || showTechConsole || showTechStats ? (
              <>
                <div className="sidebar-section">Work</div>

                <nav className="sidebar-nav">
                  {showPartsQueue ? (
                    <NavLink to="/parts" className={navLinkClass}>
                      Parts Queue
                    </NavLink>
                  ) : null}

                  {showTechConsole ? (
                    <NavLink to="/tech" className={navLinkClass}>
                      Technician Console
                    </NavLink>
                  ) : null}

                  {showTechStats ? (
                    <NavLink to="/tech-stats" className={navLinkClass}>
                      Technician Stats
                    </NavLink>
                  ) : null}
                </nav>
              </>
            ) : null}

            {showRoSearch || showShopScreen || showStaff ? (
              <>
                <div className="sidebar-section">Tools</div>

                <nav className="sidebar-nav">
                  {showRoSearch ? (
                    <NavLink to="/ro-search" className={navLinkClass}>
                      RO Search
                    </NavLink>
                  ) : null}

                  {showShopScreen ? (
                    <NavLink to="/shop-screen" className={navLinkClass}>
                      Shop Screen
                    </NavLink>
                  ) : null}

                  {showStaff ? (
                    <NavLink to="/staff" className={navLinkClass}>
                      Staff
                    </NavLink>
                  ) : null}
                </nav>
              </>
            ) : null}

            <div className="sidebar-section">Account</div>

            {isImpersonating ? (
              <button
                type="button"
                className="sidebar-btn"
                onClick={() => {
                  if (typeof auth?.stopImpersonation === "function") {
                    auth.stopImpersonation();
                  }
                }}
                style={{
                  background: "#fef3c7",
                  color: "#92400e",
                  fontWeight: 800,
                  borderColor: "#f59e0b",
                }}
              >
                Exit Impersonation
              </button>
            ) : null}

            <nav className="sidebar-nav">
              <NavLink to="/settings" className={navLinkClass}>
                Settings
              </NavLink>
            </nav>

            <button
              type="button"
              className="sidebar-btn sidebar-btn-danger"
              onClick={() => {
                if (typeof clearSession === "function") clearSession();
                nav("/login");
              }}
            >
              Log out
            </button>
          </>
        )}
      </aside>

      <main className="app-main">
        {isImpersonating ? (
          <div
            style={{
              background: "#7f1d1d",
              color: "#fff",
              padding: "10px 16px",
              fontWeight: 800,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 12,
              position: "sticky",
              top: 0,
              zIndex: 50,
              boxShadow: "0 2px 8px rgba(15,23,42,0.18)",
            }}
          >
            <span>
              Impersonating: {displayName}{" "}
              <span style={{ opacity: 0.82, fontWeight: 600 }}>({roleLine})</span>
            </span>

            <button
              type="button"
              onClick={() => {
                if (typeof auth?.stopImpersonation === "function") {
                  auth.stopImpersonation();
                }
              }}
              style={{
                background: "#fff",
                color: "#7f1d1d",
                border: "0",
                borderRadius: 8,
                padding: "7px 12px",
                fontWeight: 800,
                cursor: "pointer",
              }}
            >
              Exit Impersonation
            </button>
          </div>
        ) : null}

        <div className="app-main-inner">
          <SyncStatusBar store={store} isImpersonating={isImpersonating} />
          <Outlet />
        </div>

        <footer className="ft-appFooter">
          <div className="ft-appFooterLeft">
            © {new Date().getFullYear()} Flowtrak
          </div>
          <div className="ft-appFooterRight">
            <a className="ft-appFooterLink" href="/terms">
              Terms
            </a>
            <span className="ft-appFooterDot">•</span>
            <a className="ft-appFooterLink" href="/privacy">
              Privacy
            </a>
            <span className="ft-appFooterDot">•</span>
            <a className="ft-appFooterLink" href="/dpa">
              DPA
            </a>
            <span className="ft-appFooterDot">•</span>
            <a className="ft-appFooterLink" href="/aup">
              AUP
            </a>
            <span className="ft-appFooterDot">•</span>
            <a className="ft-appFooterLink" href="/security">
              Security
            </a>
            <span className="ft-appFooterDot">•</span>
            <a className="ft-appFooterLink" href="/subprocessors">
              Subprocessors
            </a>
          </div>
        </footer>
      </main>
    </div>
  );
}
